/**
 * Internal config: maps each service page (by route path) to its recommended
 * keyword-targeted H2 outline. When a city is supplied, headings are
 * interpolated with `{city}`. When omitted, headings fall back to "Texas".
 *
 * Used automatically by <ServicePage /> to render a consistent 5-H2 outline:
 *   1. {Service} in {City}            (page H1 — set per-page)
 *   2. Why Choose Us for {Service}    (whyUs)
 *   3. {Service} Products & Options   (products / gallery)
 *   4. Service Areas Near {City}      (applications)
 *   5. {Service} FAQs                 (faqs)
 */

export interface ServiceHeadingTemplates {
  /** Short noun phrase for the service, e.g. "Custom Awnings". */
  serviceLabel: string;
  /** Why-choose-us H2 — defaults to `Why Choose Texas Made Shade for {service} in {city}`. */
  whyUs?: (city: string) => string;
  /** Products & options gallery H2. */
  products?: (city: string) => string;
  /** Service areas / applications H2. */
  serviceAreas?: (city: string) => string;
  /** FAQs H2. */
  faqs?: (city: string) => string;
  /** Optional H1 helper (most pages set their own title). */
  hero?: (city: string) => string;
}

const TEXAS = "Texas";

/** Default templates used when a service-specific entry doesn't override. */
const placeLabel = (city: string) => city === TEXAS ? TEXAS : `${city}, TX`;

const defaults = (label: string): Required<Omit<ServiceHeadingTemplates, "serviceLabel">> => ({
  whyUs: (city) => `Why ${city} Chooses Texas Made Shade for ${label}`,
  products: (city) => `${label} Products & Options in ${city}`,
  serviceAreas: (city) => `Service Areas Near ${placeLabel(city)} for ${label}`,
  faqs: (city) => `${label} FAQs — ${placeLabel(city)}`,
  hero: (city) => `${label} in ${placeLabel(city)}`,
});

/**
 * Service-specific overrides. Keys are the route path (e.g. "/awnings").
 * Add new service routes here as they're created.
 */
const SERVICE_HEADINGS: Record<string, ServiceHeadingTemplates> = {
  "/awnings": { serviceLabel: "Custom Awnings" },
  "/fabric-awnings": { serviceLabel: "Fabric Awnings" },
  "/metal-awnings": { serviceLabel: "Metal Awnings" },
  "/retractable-awnings": { serviceLabel: "Retractable Awnings" },
  "/custom-awnings": { serviceLabel: "Custom Awnings" },
  "/commercial-awnings": { serviceLabel: "Commercial Awnings" },
  "/residential-awnings": { serviceLabel: "Residential Awnings" },
  "/recover-awnings": { serviceLabel: "Awning & Canopy Recovers" },
  "/sail-shades": { serviceLabel: "Sail Shades" },
  "/pergola-covers": { serviceLabel: "Patio, Gazebo & Pergola Covers" },
  "/curtains": { serviceLabel: "Outdoor Curtains & Drop Panels" },
  "/boat-covers": { serviceLabel: "Boat Covers & Marine Canvas" },
  "/rv-covers": { serviceLabel: "RV Covers" },
  "/trailer-covers": { serviceLabel: "Trailer Covers" },
  "/stock-trailer-covers": { serviceLabel: "Stock Trailer Covers" },
  "/wagon-covers": { serviceLabel: "Wagon Covers" },
  "/utv-atv-covers": { serviceLabel: "UTV & ATV Covers" },
  "/deer-blind-covers": { serviceLabel: "Deer Blind Covers" },
  "/hunting-fishing-covers": { serviceLabel: "Hunting & Fishing Canvas" },
  "/cooler-gun-gear-covers": { serviceLabel: "Cooler, Gun & Gear Covers" },
  "/dog-boxes": { serviceLabel: "Custom Dog Boxes & Hunting Truck Builds" },
  "/covers-tops": { serviceLabel: "Covers & Tops" },
  "/pool-spa": { serviceLabel: "Pool & Spa Covers" },
  "/schools-athletics": { serviceLabel: "School & Athletic Field Shade" },
  "/custom-fabrication": { serviceLabel: "Welding & Custom Fabrication" },
  "/fabrics": { serviceLabel: "Outdoor Fabrics" },
};

export interface ResolvedServiceHeadings {
  serviceLabel: string;
  whyUs: string;
  products: string;
  serviceAreas: string;
  faqs: string;
  hero: string;
}

/**
 * Resolve the keyword-mapped H2 outline for a service page.
 * Pass `city` to interpolate the city name; omit to default to "Texas".
 */
export function getServiceHeadings(
  servicePath: string | undefined,
  city?: string,
): ResolvedServiceHeadings | null {
  if (!servicePath) return null;
  const cfg = SERVICE_HEADINGS[servicePath];
  if (!cfg) return null;
  const label = cfg.serviceLabel;
  const d = defaults(label);
  const c = city?.trim() || TEXAS;
  return {
    serviceLabel: label,
    whyUs: (cfg.whyUs ?? d.whyUs)(c),
    products: (cfg.products ?? d.products)(c),
    serviceAreas: (cfg.serviceAreas ?? d.serviceAreas)(c),
    faqs: (cfg.faqs ?? d.faqs)(c),
    hero: (cfg.hero ?? d.hero)(c),
  };
}

export { SERVICE_HEADINGS };
